import { useState, useEffect } from 'react';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const countryCodes = [
  { code: '+1', country: 'US/CA', flag: '🇺🇸' },
  { code: '+44', country: 'UK', flag: '🇬🇧' },
  { code: '+34', country: 'ES', flag: '🇪🇸' },
  { code: '+33', country: 'FR', flag: '🇫🇷' },
  { code: '+49', country: 'DE', flag: '🇩🇪' },
  { code: '+39', country: 'IT', flag: '🇮🇹' },
  { code: '+351', country: 'PT', flag: '🇵🇹' },
  { code: '+55', country: 'BR', flag: '🇧🇷' },
  { code: '+52', country: 'MX', flag: '🇲🇽' },
  { code: '+81', country: 'JP', flag: '🇯🇵' },
  { code: '+82', country: 'KR', flag: '🇰🇷' },
  { code: '+86', country: 'CN', flag: '🇨🇳' },
  { code: '+91', country: 'IN', flag: '🇮🇳' },
  { code: '+61', country: 'AU', flag: '🇦🇺' },
  { code: '+64', country: 'NZ', flag: '🇳🇿' },
  { code: '+7', country: 'RU', flag: '🇷🇺' },
  { code: '+31', country: 'NL', flag: '🇳🇱' },
  { code: '+32', country: 'BE', flag: '🇧🇪' },
  { code: '+41', country: 'CH', flag: '🇨🇭' },
  { code: '+46', country: 'SE', flag: '🇸🇪' },
  { code: '+47', country: 'NO', flag: '🇳🇴' },
  { code: '+45', country: 'DK', flag: '🇩🇰' },
  { code: '+48', country: 'PL', flag: '🇵🇱' },
  { code: '+43', country: 'AT', flag: '🇦🇹' },
  { code: '+420', country: 'CZ', flag: '🇨🇿' },
  { code: '+36', country: 'HU', flag: '🇭🇺' },
  { code: '+30', country: 'GR', flag: '🇬🇷' },
  { code: '+90', country: 'TR', flag: '🇹🇷' },
  { code: '+972', country: 'IL', flag: '🇮🇱' },
  { code: '+971', country: 'AE', flag: '🇦🇪' },
  { code: '+966', country: 'SA', flag: '🇸🇦' },
  { code: '+27', country: 'ZA', flag: '🇿🇦' },
  { code: '+234', country: 'NG', flag: '🇳🇬' },
  { code: '+20', country: 'EG', flag: '🇪🇬' },
  { code: '+254', country: 'KE', flag: '🇰🇪' },
  { code: '+63', country: 'PH', flag: '🇵🇭' },
  { code: '+66', country: 'TH', flag: '🇹🇭' },
  { code: '+65', country: 'SG', flag: '🇸🇬' },
  { code: '+60', country: 'MY', flag: '🇲🇾' },
  { code: '+62', country: 'ID', flag: '🇮🇩' },
  { code: '+84', country: 'VN', flag: '🇻🇳' },
  { code: '+57', country: 'CO', flag: '🇨🇴' },
  { code: '+54', country: 'AR', flag: '🇦🇷' },
  { code: '+56', country: 'CL', flag: '🇨🇱' },
  { code: '+51', country: 'PE', flag: '🇵🇪' },
  { code: '+58', country: 'VE', flag: '🇻🇪' },
];

interface PhoneInputProps {
  value: string;
  onChange: (fullNumber: string) => void;
  placeholder?: string;
}

export const PhoneInput = ({ value, onChange, placeholder = '(555) 123-4567' }: PhoneInputProps) => {
  const [countryCode, setCountryCode] = useState('+1');
  const [localNumber, setLocalNumber] = useState('');

  // Parse incoming value to extract country code and local number
  useEffect(() => {
    if (value) {
      const matchingCountry = countryCodes.find(c => value.startsWith(c.code));
      if (matchingCountry) {
        setCountryCode(matchingCountry.code);
        setLocalNumber(value.slice(matchingCountry.code.length));
      } else if (value.startsWith('+')) {
        // Try to extract unknown country code
        const match = value.match(/^(\+\d{1,4})(.*)$/);
        if (match) {
          setCountryCode(match[1]);
          setLocalNumber(match[2]);
        }
      } else {
        setLocalNumber(value);
      }
    }
  }, []);

  const formatPhoneNumber = (input: string) => {
    // Remove all non-digit characters
    const digits = input.replace(/\D/g, '');
    return digits;
  };

  const handleLocalChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatPhoneNumber(e.target.value);
    setLocalNumber(formatted);
    onChange(countryCode + formatted);
  };

  const handleCountryChange = (newCode: string) => {
    setCountryCode(newCode);
    onChange(newCode + localNumber);
  };

  const selectedCountry = countryCodes.find(c => c.code === countryCode);

  return (
    <div className="flex gap-2">
      <Select value={countryCode} onValueChange={handleCountryChange}>
        <SelectTrigger className="w-[110px] flex-shrink-0">
          <SelectValue>
            {selectedCountry ? (
              <span className="flex items-center gap-1">
                <span>{selectedCountry.flag}</span>
                <span className="text-xs">{selectedCountry.code}</span>
              </span>
            ) : (
              countryCode
            )}
          </SelectValue>
        </SelectTrigger>
        <SelectContent className="max-h-[300px] bg-background">
          {countryCodes.map((country) => (
            <SelectItem key={country.code} value={country.code}>
              <span className="flex items-center gap-2">
                <span>{country.flag}</span>
                <span>{country.code}</span>
                <span className="text-muted-foreground text-xs">{country.country}</span>
              </span>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Input
        type="tel"
        placeholder={placeholder}
        value={localNumber}
        onChange={handleLocalChange}
        className="flex-1"
      />
    </div>
  );
};
